import { assignPhotoFileNames } from "@rebuild/shared";
import type { ResponsiveNotePhoto } from "@rebuild/shared/models";
import { ResponsiveNotePhotoMappingStatus } from "@rebuild/shared/models";
import type { BoundingBox, EstimateDetail, Prediction } from "@/interfaces/estimate-detail";
import type { File } from "@/interfaces/estimation.interface";
import { getStorageFileUrl } from "@/util/get-storage-file-url";
import type { PhotoGalleryBuildParams, PhotoGalleryGroup, PhotoGalleryItem } from "./photos.types";
import { zipSync } from "fflate";

const RESPONSIVE_NOTE_GENERAL_GROUP_ID = "responsive-note-general";
const RESPONSIVE_NOTE_UNMATCHED_GROUP_ID = "responsive-note-unmatched";

const RESPONSIVE_NOTE_SPECIAL_GROUP_ORDER = [RESPONSIVE_NOTE_UNMATCHED_GROUP_ID];

export function normalizeEstimateFilePhoto(photo: File): PhotoGalleryItem | null {
  if (!photo.uploadId) {
    return null;
  }

  return {
    galleryId: `estimate-file:${photo.uploadId}`,
    source: "estimate-file",
    imageUrl: photo.remotePath,
    roomId: getEstimateFileGroupId(photo),
    roomName: photo.metadata?.roomName,
    file: photo,
    predictionSourceUploadId: photo.uploadId,
  };
}

export function normalizeResponsiveNotePhoto(photo: ResponsiveNotePhoto): PhotoGalleryItem {
  return {
    galleryId: `responsive-note:${photo.id}`,
    source: "responsive-note",
    imageUrl: photo.downloadUrl || photo.storagePath,
    thumbnailUrl: photo.thumbnailStoragePath,
    roomId: photo.scopeNoteRoomId || undefined,
    roomPath: photo.roomPath,
    createdAt: photo.createdAt,
    responsiveNotePhoto: photo,
  };
}

export function isPhotoGalleryItem(photo: PhotoGalleryItem | null | undefined): photo is PhotoGalleryItem {
  return Boolean(photo);
}

export function getPhotoImageUrl(photo: PhotoGalleryItem, preferThumbnail = false): string {
  return resolvePhotoUrl(preferThumbnail ? (photo.thumbnailUrl ?? photo.imageUrl) : photo.imageUrl);
}

function resolvePhotoUrl(imageUrl: string): string {
  if (/^(https?:|blob:|data:)/.test(imageUrl)) {
    return imageUrl;
  }
  return getStorageFileUrl(imageUrl);
}

export function getEstimateFileRoomId(file: File): string | undefined {
  return file.metadata?.assignedRoomId || file.metadata?.clusterId || undefined;
}

export function sortEstimateFilesByRoomOrder(files: File[], rooms: EstimateDetail[]): File[] {
  const roomOrderById = buildRoomOrderById(rooms);

  return files
    .map((file, index) => ({ file, index }))
    .sort((a, b) => {
      const byRoomOrder = compareRoomOrder(
        roomOrderById.get(getEstimateFileGroupId(a.file)),
        roomOrderById.get(getEstimateFileGroupId(b.file)),
      );
      if (byRoomOrder !== undefined) {
        return byRoomOrder;
      }

      const roomNameCompare = (a.file.metadata?.roomName || "").localeCompare(b.file.metadata?.roomName || "");
      return roomNameCompare || a.index - b.index;
    })
    .map(({ file }) => file);
}

export function buildPhotoGalleryGroups({ estimatePhotos, responsiveNotePhotos, rooms }: PhotoGalleryBuildParams): PhotoGalleryGroup[] {
  const roomById = new Map(rooms.map((room) => [room.dataForUI.metadata.pageId, room]));
  const roomOrderById = buildRoomOrderById(rooms);
  const generalRoom = rooms.find((room) => isGeneralRoomName(room.dataForUI.metadata.roomName) && !room.isUnmatchedCluster);
  const groups = new Map<string, PhotoGalleryGroup>();

  const addToGroup = (group: PhotoGalleryGroup, photo: PhotoGalleryItem) => {
    const existing = groups.get(group.id);
    if (existing) {
      existing.photos.push(photo);
      if (!existing.title && group.title) {
        existing.title = group.title;
      }
      existing.isUnassignedOrMappedByUser = existing.isUnassignedOrMappedByUser || group.isUnassignedOrMappedByUser;
      return;
    }

    groups.set(group.id, { ...group, photos: [photo] });
  };

  for (const photo of estimatePhotos) {
    if (!photo) continue;
    const groupId = photo.roomId || "";
    const room = groupId ? roomById.get(groupId) : undefined;
    const title = room
      ? room.isUnmatchedCluster
        ? `Unassigned - ${room.dataForUI.metadata.roomName}`
        : room.dataForUI.metadata.roomName
      : photo.roomName;

    addToGroup(
      {
        id: groupId,
        title,
        isUnassignedOrMappedByUser: room?.isUnmatchedCluster || (!room && Boolean(groupId)),
        photos: [],
      },
      photo,
    );
  }

  for (const photo of responsiveNotePhotos) {
    if (!photo?.responsiveNotePhoto) continue;
    addToGroup(resolveResponsiveNotePhotoGroup(photo, roomById, generalRoom), photo);
  }

  return Array.from(groups.values()).sort((a, b) => comparePhotoGroups(a, b, roomOrderById));
}

/** The capture timestamp (Unix ms) shown for a photo, if known. */
export function getPhotoCapturedAtMs(item: PhotoGalleryItem): number | undefined {
  if (item.source === "responsive-note") {
    return item.responsiveNotePhoto?.capturedAt ?? item.createdAt;
  }
  const uploadDate = item.file?.uploadDate;
  if (!uploadDate) {
    return undefined;
  }
  // Only trust values that carry an explicit time component (ISO datetime). Some estimate files
  // store a date-only `uploadDate` (e.g. "6/1/2026" from the webapp upload path); parsing those
  // yields local midnight, which would render as a misleading "12:00 AM". Treat them as unknown.
  const parsed = /^\d{4}-\d{2}-\d{2}T/.test(uploadDate) ? Date.parse(uploadDate) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : undefined;
}

/** Format a capture timestamp, e.g. "Jun 3, 2026 · 8:12 AM". Empty when unknown. */
export function formatPhotoNumberOverlayTimestamp(capturedAtMs: number | undefined): string {
  if (capturedAtMs === undefined) {
    return "";
  }
  return `${formatPhotoDate(capturedAtMs)} · ${formatPhotoTime(capturedAtMs)}`;
}

/** e.g. "8:12 AM". Empty when unknown. */
export function formatPhotoTime(capturedAtMs: number | undefined): string {
  if (capturedAtMs === undefined) {
    return "";
  }
  return new Date(capturedAtMs).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
}

function formatPhotoDate(capturedAtMs: number, options: Intl.DateTimeFormatOptions = {}): string {
  return new Date(capturedAtMs).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", ...options });
}

interface PhotoDayRun {
  key: string;
  /** e.g. "Tue, Jun 3, 2026"; undefined for photos with no known capture time. */
  label?: string;
  photos: PhotoGalleryItem[];
}

/** Orders each group's photos by capture time (stable, undated last) without touching group order. */
export function sortGroupPhotosByCapturedAt(groups: PhotoGalleryGroup[]): PhotoGalleryGroup[] {
  return groups.map((group) => ({
    ...group,
    // Stable sort; Infinity - Infinity is NaN, which sort treats as a tie.
    photos: [...group.photos].sort(
      (a, b) => (getPhotoCapturedAtMs(a) ?? Number.POSITIVE_INFINITY) - (getPhotoCapturedAtMs(b) ?? Number.POSITIVE_INFINITY),
    ),
  }));
}

/** Splits already-sorted photos into consecutive runs that share a local calendar day. */
export function splitPhotosByCapturedDay(photos: PhotoGalleryItem[]): PhotoDayRun[] {
  const runs: PhotoDayRun[] = [];
  for (const photo of photos) {
    const capturedAt = getPhotoCapturedAtMs(photo);
    const date = capturedAt === undefined ? undefined : new Date(capturedAt);
    const key = date ? `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}` : "unknown";
    const lastRun = runs[runs.length - 1];
    if (lastRun?.key === key) {
      lastRun.photos.push(photo);
      continue;
    }
    runs.push({ key, label: capturedAt === undefined ? undefined : formatPhotoDate(capturedAt, { weekday: "short" }), photos: [photo] });
  }
  return runs;
}

export function highestScoringPredictionForFile(fileId: string, predictions?: Prediction[]) {
  const predictionsForFile = (predictions || []).filter((el) => el.sourceUploadId === fileId) || [];
  return predictionsForFile.sort((a, b) => (a.confidence || 0) - (b.confidence || 0)).pop();
}

export function descriptionForPrediction(prediction?: Prediction) {
  return prediction && typeof prediction.confidence === "number" && Number.isFinite(prediction.confidence) && prediction.objectId
    ? `${Math.round(prediction.confidence * 100)}% confidence of ${prediction.objectId.replace("_", " ")}`
    : "";
}

export function boxForPrediction(prediction?: Prediction): BoundingBox | undefined {
  const box = prediction?.boundingBox;
  const validBox =
    box &&
    [box.left, box.top, box.width, box.height].every((value) => typeof value === "number" && Number.isFinite(value)) &&
    box.width > 0 &&
    box.height > 0;
  return validBox
    ? {
        left: box.left,
        top: box.top,
        width: box.width,
        height: box.height,
      }
    : undefined;
}

function resolveResponsiveNotePhotoGroup(
  photo: PhotoGalleryItem,
  roomsById: Map<string, EstimateDetail>,
  generalRoom: EstimateDetail | undefined,
): PhotoGalleryGroup {
  const responsivePhoto = photo.responsiveNotePhoto!;
  if (photo.roomId) {
    const room = roomsById.get(photo.roomId);
    if (room) {
      return {
        id: photo.roomId,
        title: room.dataForUI.metadata.roomName,
        isUnassignedOrMappedByUser: room.isUnmatchedCluster,
        photos: [],
      };
    }
    return getResponsiveNoteUnmatchedGroup();
  }

  if (responsivePhoto.mappingStatus === ResponsiveNotePhotoMappingStatus.pending || !responsivePhoto.mappingStatus) {
    return getResponsiveNoteUnmatchedGroup();
  }

  if (responsivePhoto.parentBlockId === null && responsivePhoto.mappingStatus === ResponsiveNotePhotoMappingStatus.mapped) {
    if (generalRoom) {
      return {
        id: generalRoom.dataForUI.metadata.pageId,
        title: generalRoom.dataForUI.metadata.roomName,
        photos: [],
      };
    }

    return {
      id: RESPONSIVE_NOTE_GENERAL_GROUP_ID,
      title: "General",
      photos: [],
    };
  }

  return getResponsiveNoteUnmatchedGroup();
}

function getResponsiveNoteUnmatchedGroup(): PhotoGalleryGroup {
  return {
    id: RESPONSIVE_NOTE_UNMATCHED_GROUP_ID,
    title: "Unmatched",
    photos: [],
  };
}

function comparePhotoGroups(a: PhotoGalleryGroup, b: PhotoGalleryGroup, roomOrderById: Map<string, number>): number {
  const generalA = isGeneralPhotoGroup(a);
  const generalB = isGeneralPhotoGroup(b);
  if (generalA || generalB) {
    if (generalA && generalB) {
      return compareByRoomOrderThenTitle(a, b, roomOrderById);
    }
    return generalA ? -1 : 1;
  }

  const specialA = RESPONSIVE_NOTE_SPECIAL_GROUP_ORDER.indexOf(a.id);
  const specialB = RESPONSIVE_NOTE_SPECIAL_GROUP_ORDER.indexOf(b.id);
  if (specialA !== -1 || specialB !== -1) {
    if (specialA === -1) return -1;
    if (specialB === -1) return 1;
    return specialA - specialB;
  }

  return compareByRoomOrderThenTitle(a, b, roomOrderById);
}

function compareByRoomOrderThenTitle(a: PhotoGalleryGroup, b: PhotoGalleryGroup, roomOrderById: Map<string, number>): number {
  const byRoomOrder = compareRoomOrder(roomOrderById.get(a.id), roomOrderById.get(b.id));
  return byRoomOrder ?? (a.title || "").localeCompare(b.title || "");
}

function buildRoomOrderById(rooms: EstimateDetail[]): Map<string, number> {
  return new Map(rooms.map((room, index) => [room.dataForUI.metadata.pageId, index]));
}

/**
 * Orders by the room's position in the estimate. Returns undefined when that can't decide it —
 * neither side belongs to a known room, or both sit in the same one — so callers apply their own
 * tie-breaker.
 */
function compareRoomOrder(orderA: number | undefined, orderB: number | undefined): number | undefined {
  if (orderA !== undefined && orderB !== undefined) {
    return orderA - orderB || undefined;
  }
  if (orderA !== undefined) {
    return -1;
  }
  if (orderB !== undefined) {
    return 1;
  }
  return undefined;
}

function isGeneralPhotoGroup(group: PhotoGalleryGroup): boolean {
  return group.id === RESPONSIVE_NOTE_GENERAL_GROUP_ID || isGeneralRoomName(group.title);
}

function isGeneralRoomName(roomName: string | undefined): boolean {
  return Boolean(roomName?.match(/general/i));
}

/** Group key for an estimate file: unassigned files share the empty-string group. */
function getEstimateFileGroupId(file: File): string {
  return getEstimateFileRoomId(file) || "";
}

/** Downloading every photo in an estimate can be hundreds of files, so they go out in batches. */
const DOWNLOAD_CONCURRENCY = 6;

interface PhotoDownload {
  name: string;
  url: string;
  mimeType: string;
}

interface FetchedPhoto extends PhotoDownload {
  data: Uint8Array;
}

/**
 * File names by photo id, matching what the Xactimate export calls each photo (`Kitchen-01.jpg`).
 * Pass every note photo on the estimate, not just a selection: numbering runs per room across the
 * whole set in capture order, so a partial download still gets the names Xactimate shows.
 * Labels ride along after the room (`Kitchen-Air-Mover-01.jpg`) with no Xactimate length cap.
 */
export function buildPhotoDownloadNames(notePhotos: ResponsiveNotePhoto[], rooms: EstimateDetail[]): Map<string, string> {
  const roomNameByPageId = new Map(rooms.map((room) => [room.dataForUI.metadata.pageId, room.dataForUI.metadata.roomName]));
  // Same order the export numbers in (ResponsiveNotePhotoService.listEstimatePhotos).
  const ordered = [...notePhotos].sort((a, b) => a.capturedAt - b.capturedAt);
  const names = assignPhotoFileNames(ordered, roomNameByPageId);
  return new Map(ordered.map((photo, i) => [photo.id, `${names[i]}${storageExtension(photo.storagePath)}`]));
}

function storageExtension(storagePath: string): string {
  return storagePath.match(/\.[^./]+$/)?.[0] ?? ".jpg";
}

/** Download photos as a zip file or individual files, named per {@link buildPhotoDownloadNames}. */
export const downloadPhotos = async (photos: PhotoGalleryItem[], zipFileName: string, fileNames: Map<string, string>) => {
  const downloads = photos.map((photo) => toPhotoDownload(photo, fileNames)).filter((download): download is PhotoDownload => download !== undefined);

  const fetched = await fetchPhotos(downloads);

  if (fetched.length === 1) {
    const [photo] = fetched;
    saveBlob(new Blob([photo.data], { type: photo.mimeType }), photo.name);
  } else if (fetched.length > 1) {
    const archive = zipSync(Object.fromEntries(fetched.map((photo) => [photo.name, photo.data])), { level: 0 });
    
    saveBlob(new Blob([archive], { type: "application/zip" }), zipFileName);
  }

  return { downloadedCount: fetched.length, failedCount: downloads.length - fetched.length };
}

/**
 * Fetch every photo, keeping the input order. A failure is dropped rather than thrown so one
 * expired signed URL does not lose the rest of the batch.
 */
const fetchPhotos = async (downloads: PhotoDownload[]): Promise<FetchedPhoto[]> => {
  const fetched = new Array<FetchedPhoto | undefined>(downloads.length);
  let nextIndex = 0;

  const worker = async () => {
    while (nextIndex < downloads.length) {
      const index = nextIndex++;
      const download = downloads[index];
      try {
        fetched[index] = { ...download, data: await fetchBytes(download.url) };
      } catch {
        // Counted as a failure by the caller, which compares the fetched count against the input.
      }
    }
  };

  await Promise.all(Array.from({ length: Math.min(DOWNLOAD_CONCURRENCY, downloads.length) }, worker));
  return fetched.filter((photo): photo is FetchedPhoto => photo !== undefined);
}

/** Convert a photo to a download object. */
const toPhotoDownload = (photo: PhotoGalleryItem, fileNames: Map<string, string>): PhotoDownload | undefined => {
  const {id, storagePath, mimeType} = photo.responsiveNotePhoto ?? {};

  if (!id || !storagePath) {
    return undefined;
  }

  return {
    // The id fallback keeps zip entries distinct if a photo arrived after the names were built.
    name: fileNames.get(id) ?? `${id}${storageExtension(storagePath)}`,
    url: resolvePhotoUrl(storagePath),
    mimeType: mimeType ?? "image/jpeg",
  };
}

/** Fetch bytes from a URL. */
const fetchBytes = async (url: string): Promise<Uint8Array> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Photo download failed: ${response.status}`);
  }
  return new Uint8Array(await response.arrayBuffer());
}

/** Save a blob to the browser. */
const saveBlob = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/** True when a keypress is aimed at a text field or an open picker, which should keep its own keys. */
export function isEditableKeyboardTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  return target.isContentEditable || Boolean(target.closest("input, textarea, select, [role='combobox'], [role='listbox']"));
}
